/* Florence website chat. Public configuration only; provider credentials stay server-side. */
const SESSION_PREFIX = 'tlw.florence.website.v1:';
const MAX_MESSAGE = 2000;
const MAX_REPLY = 16000;

export function resolveBackend(value, location) {
  const raw = String(value || '').trim();
  if (!raw || /[?#\\]/.test(raw) || raw.startsWith('//')) return null;
  if (!raw.startsWith('/') && !/^https:\/\//i.test(raw)) return null;
  try {
    const url = new URL(raw, location.href);
    if (url.username || url.password) return null;
    const localPreview = ['127.0.0.1', 'localhost', '[::1]'].includes(location.hostname);
    if (url.protocol !== 'https:' && !(localPreview && url.origin === location.origin && url.protocol === 'http:')) return null;
    if (raw.startsWith('/') && url.origin !== location.origin) return null;
    return url.href.replace(/\/$/, '');
  } catch { return null; }
}

export function newRequestId(crypto) {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto?.getRandomValues !== 'function') throw new Error('SECURE_RANDOM_UNAVAILABLE');
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function normaliseMessage(value) {
  const text = String(value || '').trim();
  if (!text || text.length > MAX_MESSAGE) throw new Error('INVALID_MESSAGE');
  return text;
}

function validSession(value) {
  return value && typeof value.sessionToken === 'string' && value.sessionToken.length >= 16 && value.sessionToken.length <= 8192 &&
    typeof value.conversationId === 'string' && value.conversationId.length > 0 && value.conversationId.length <= 200;
}

export function createChatClient({ backend, fetcher, storage, crypto }) {
  const storageKey = SESSION_PREFIX + backend;
  let session = null;
  let sessionPromise = null;
  let pending = false;
  const recoveredRequests = new Set();
  const uncertainRequests = new Set();
  const stoppedRequests = new Set();
  try {
    const saved = JSON.parse(storage?.getItem(storageKey) || 'null');
    if (validSession(saved)) session = saved;
  } catch { /* Storage can be unavailable in a private browser. */ }
  async function post(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);
    try {
      const response = await fetcher(backend + path, {
        method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body), signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error('CHAT_REQUEST_FAILED');
        error.status = response.status;
        if (response.status === 401) {
          try { if ((await response.json()).error === 'SESSION_EXPIRED') error.code = 'SESSION_EXPIRED'; }
          catch { /* A malformed rejection cannot authorise a session reset. */ }
        }
        throw error;
      }
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  async function establishSession() {
    if (session) return session;
    if (!sessionPromise) sessionPromise = post('/api/website-chat/session', {}).then(result => {
      if (!validSession(result)) throw new Error('INVALID_SESSION');
      session = { sessionToken: result.sessionToken, conversationId: result.conversationId };
      try { storage?.setItem(storageKey, JSON.stringify(session)); } catch { /* Keep memory-only session. */ }
      return session;
    }).finally(() => { sessionPromise = null; });
    return sessionPromise;
  }
  function forgetSession() {
    session = null;
    try { storage?.removeItem(storageKey); } catch { /* Memory-only session is already cleared. */ }
  }
  function stopRequest(requestId, code) {
    stoppedRequests.add(requestId);
    const error = new Error(code);
    error.code = code;
    error.retryable = false;
    return error;
  }
  async function sendMessage(request, current, message) {
    try {
      return await post('/api/website-chat/messages', { sessionToken: current.sessionToken, requestId: request.requestId, message });
    } catch (error) {
      if (error.status === undefined || error.status >= 500) uncertainRequests.add(request.requestId);
      throw error;
    }
  }
  return {
    prepare(value) { return { requestId: newRequestId(crypto), message: normaliseMessage(value) }; },
    async send(request, { onSessionReset } = {}) {
      if (pending) throw new Error('REQUEST_PENDING');
      if (!request || !/^[a-f0-9-]{36}$/i.test(request.requestId)) throw new Error('INVALID_REQUEST_ID');
      if (stoppedRequests.has(request.requestId)) throw stopRequest(request.requestId, 'SESSION_RECOVERY_STOPPED');
      const message = normaliseMessage(request.message);
      pending = true;
      try {
        let current = await establishSession();
        let result;
        let sessionReset = false;
        try { result = await sendMessage(request, current, message); }
        catch (error) {
          if (error.status !== 401 || error.code !== 'SESSION_EXPIRED') throw error;
          forgetSession();
          if (uncertainRequests.has(request.requestId)) throw stopRequest(request.requestId, 'EXPIRED_AFTER_UNCONFIRMED_SEND');
          if (recoveredRequests.has(request.requestId)) throw stopRequest(request.requestId, 'SESSION_RECOVERY_STOPPED');
          recoveredRequests.add(request.requestId);
          sessionReset = true;
          try { onSessionReset?.(); } catch { /* UI notification cannot authorise or block a send. */ }
          current = await establishSession();
          try { result = await sendMessage(request, current, message); }
          catch (retryError) {
            if (retryError.status === 401 && retryError.code === 'SESSION_EXPIRED') {
              forgetSession();
              throw stopRequest(request.requestId, 'SESSION_RECOVERY_STOPPED');
            }
            throw retryError;
          }
        }
        if (!result || typeof result.reply !== 'string' || !result.reply.trim() || result.reply.length > MAX_REPLY ||
          typeof result.status !== 'string' || result.conversationId !== current.conversationId) {
          uncertainRequests.add(request.requestId);
          throw new Error('INVALID_CHAT_REPLY');
        }
        return { reply: result.reply, status: result.status, conversationId: result.conversationId, ...(sessionReset ? { sessionReset: true } : {}) };
      } finally { pending = false; }
    }
  };
}

function element(document, tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function mountFlorenceChat(root, environment = globalThis) {
  if (root.dataset.lwChatMounted === 'true') return null;
  if (root.dataset.enabled !== 'true' && root.dataset.preview !== 'true') return null;
  const { document, location } = environment;
  const backend = resolveBackend(root.dataset.backend, location);
  root.dataset.lwChatMounted = 'true';
  root.classList.add('lw-florence');
  const id = 'lw-florence-' + newRequestId(environment.crypto);
  const preview = root.dataset.preview === 'true';
  let client;
  try { client = backend ? createChatClient({ backend, fetcher: environment.fetch.bind(environment), storage: environment.sessionStorage, crypto: environment.crypto }) : null; }
  catch { client = backend ? createChatClient({ backend, fetcher: environment.fetch.bind(environment), storage: null, crypto: environment.crypto }) : null; }
  const launcher = element(document, 'button', 'lw-florence__launcher');
  launcher.type = 'button';
  launcher.setAttribute('aria-expanded', 'false');
  launcher.setAttribute('aria-controls', id);
  launcher.setAttribute('aria-label', 'Open chat with Florence, The Lamp Workshop sales assistant');
  const star = element(document, 'span', 'lw-florence__spark', '✦');
  star.setAttribute('aria-hidden', 'true');
  launcher.append(star, element(document, 'span', '', 'Ask Florence'));
  const panel = element(document, 'section', 'lw-florence__panel');
  panel.id = id;
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'false');
  panel.setAttribute('aria-labelledby', id + '-title');
  const header = element(document, 'header', 'lw-florence__header');
  const heading = element(document, 'div');
  heading.append(element(document, 'p', 'lw-florence__eyebrow', 'THE LAMP WORKSHOP'));
  const title = element(document, 'h2', 'lw-florence__title', 'Florence');
  title.id = id + '-title';
  heading.append(title, element(document, 'p', 'lw-florence__subtitle', 'Sales & Client Services · AI assistant'));
  const close = element(document, 'button', 'lw-florence__close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close Florence chat');
  header.append(heading, close);
  panel.append(header);
  if (preview) panel.append(element(document, 'p', 'lw-florence__preview', 'Private preview • synthetic test data'));
  const transcript = element(document, 'div', 'lw-florence__transcript');
  transcript.setAttribute('role', 'log');
  transcript.setAttribute('aria-live', 'polite');
  transcript.setAttribute('aria-relevant', 'additions');
  transcript.setAttribute('aria-label', 'Conversation with Florence');
  transcript.tabIndex = 0;
  function addMessage(text, author) {
    const bubble = element(document, 'div', 'lw-florence__message lw-florence__message--' + author);
    bubble.append(element(document, 'span', 'lw-florence__author', author === 'customer' ? 'You' : 'Florence'), element(document, 'p', '', text));
    transcript.append(bubble);
    transcript.scrollTop = transcript.scrollHeight;
  }
  addMessage(client ? 'Hello, I’m Florence. How can I help with your lighting? You can ask about products, finishes or ordering.' : 'Chat is currently unavailable. Please use our contact page and we’ll be happy to help.', 'assistant');
  panel.append(transcript);
  const status = element(document, 'p', 'lw-florence__status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const form = element(document, 'form', 'lw-florence__form');
  const label = element(document, 'label', 'lw-florence__sr-only', 'Your message to Florence');
  label.htmlFor = id + '-message';
  const input = element(document, 'textarea', 'lw-florence__input');
  input.id = id + '-message';
  input.rows = 2;
  input.maxLength = MAX_MESSAGE;
  input.placeholder = 'Ask about your lighting…';
  input.disabled = !client;
  input.autocomplete = 'off';
  input.setAttribute('aria-describedby', id + '-privacy');
  const send = element(document, 'button', 'lw-florence__send', 'Send');
  send.type = 'submit';
  send.disabled = !client;
  send.setAttribute('aria-label', 'Send message to Florence');
  const retry = element(document, 'button', 'lw-florence__retry', 'Retry message');
  retry.type = 'button';
  retry.hidden = true;
  form.append(label, input, send);
  const footer = element(document, 'footer', 'lw-florence__footer');
  const privacy = element(document, 'p', '', 'Please don’t share payment details or passwords.');
  privacy.id = id + '-privacy';
  const contact = element(document, 'a', 'lw-florence__contact', 'Contact our team');
  contact.href = '/pages/contact';
  footer.append(privacy, contact);
  panel.append(status, retry, form, footer);
  root.append(panel, launcher);
  let busy = false;
  let failedRequest = null;
  function open() {
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    (client ? input : close).focus();
  }
  function dismiss() {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus();
  }
  launcher.addEventListener('click', () => panel.hidden ? open() : dismiss());
  close.addEventListener('click', dismiss);
  panel.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); dismiss(); } });
  async function deliver(request) {
    if (busy || !client) return;
    busy = true;
    send.disabled = true;
    input.disabled = true;
    retry.hidden = true;
    status.textContent = 'Florence is checking the approved information…';
    panel.setAttribute('aria-busy', 'true');
    try {
      const answer = await client.send(request, { onSessionReset() {
        const notice = element(document, 'p', 'lw-florence__status', 'Your previous chat session expired. A new conversation is starting; please repeat any earlier product details Florence needs.');
        notice.setAttribute('role', 'status');
        transcript.append(notice);
        transcript.scrollTop = transcript.scrollHeight;
        status.textContent = 'Starting a new conversation and retrying this message once…';
      } });
      addMessage(answer.reply, 'assistant');
      failedRequest = null;
      status.textContent = answer.sessionReset ? 'New conversation started. Earlier chat context is not available in this session.' : '';
    } catch (error) {
      failedRequest = request;
      status.textContent = error.code === 'EXPIRED_AFTER_UNCONFIRMED_SEND'
        ? 'Your earlier reply could not be confirmed and the session expired. Please contact our team; this message has not been resent in a new conversation.'
        : error.retryable === false ? 'The chat session could not be restored safely. Please contact our team.'
          : 'Your reply could not be confirmed. Retry this message, or contact our team.';
      retry.hidden = error.retryable === false;
    } finally {
      busy = false;
      send.disabled = false;
      input.disabled = false;
      panel.setAttribute('aria-busy', 'false');
      if (!panel.hidden) input.focus();
    }
  }
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (busy || !client) return;
    if (failedRequest) { status.textContent = 'Please retry the unconfirmed message before sending another.'; retry.focus(); return; }
    let request;
    try { request = client.prepare(input.value); }
    catch { status.textContent = 'Please enter a message of up to 2,000 characters.'; input.focus(); return; }
    addMessage(request.message, 'customer');
    input.value = '';
    void deliver(request);
  });
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); form.requestSubmit(); }
  });
  retry.addEventListener('click', () => { if (failedRequest) void deliver(failedRequest); });
  return { open, close: dismiss, backend };
}

if (typeof document !== 'undefined') {
  const mount = () => document.querySelectorAll('[data-lw-florence-chat]').forEach(root => mountFlorenceChat(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
}
